import type { FinancePartnerRow, FinancingProduct, PriceCell } from '../../../../store/oneButtonStore';
import { formatSolarRatePerKwh, solarRateFromOffer } from './utils/finance';

const FINANCING_PRODUCTS: FinancingProduct[] = [
    'Solar',
    'Roof',
    'Solar + Roof',
    'Battery',
    'Battery + Roof',
    'Solar + Battery',
];

const PARTNER_GRID_LABELS: Record<string, string> = {
    Cash: 'Cash',
    GoodLeap: 'Good Leap',
    IGS: 'IGS',
    LightReach: 'Lightreach',
    Enfin: 'Enfin',
};

export type CombinedFinancePick = {
    cellKey: string;
    partner: string;
    product: FinancingProduct;
    cell: PriceCell;
    isCash: boolean;
};

export type CombinedFinanceLine = {
    id: string;
    partner: string;
    partnerLabel: string;
    method: string;
    monthly: number;
    productTags: FinancingProduct[];
    productLabel: string;
    detail: string;
    picks: CombinedFinancePick[];
};

export type CombinedFinancingSummary = {
    totalMonthly: number;
    lines: CombinedFinanceLine[];
    breakdown: string;
};

export function partnerLabelForCombined(partner: string): string {
    return PARTNER_GRID_LABELS[partner] ?? partner;
}

function productDisplayName(product: FinancingProduct): string {
    if (product === 'Solar') return 'Solar system';
    return product;
}

/** Atomic product types absorbed by a compound column (for combined summary display). */
function productsCoveredByCompound(compound: FinancingProduct): FinancingProduct[] {
    switch (compound) {
        case 'Solar + Roof':
            return ['Solar', 'Roof'];
        case 'Battery + Roof':
            return ['Battery', 'Roof'];
        case 'Solar + Battery':
            return ['Solar', 'Battery'];
        default:
            return [];
    }
}

/** Drop e.g. Solar when Solar + Battery is also recommended, regardless of partner. */
function filterRedundantProductPicks(picks: CombinedFinancePick[]): CombinedFinancePick[] {
    const covered = new Set<FinancingProduct>();
    for (const pick of picks) {
        for (const atomic of productsCoveredByCompound(pick.product)) {
            covered.add(atomic);
        }
    }
    return picks.filter((pick) => !covered.has(pick.product));
}

function formatPickDetail(cell: PriceCell, isCash: boolean): string {
    if (isCash) return 'one-time payment';
    const parts: string[] = [];
    if (cell.escalator != null && cell.escalator > 0) {
        parts.push(`${cell.escalator}% escalator`);
    } else if (cell.apr != null && cell.apr > 0) {
        parts.push(`${cell.apr}% APR`);
    }
    if (cell.term > 0) parts.push(`${cell.term} yr`);
    const solarRate = solarRateFromOffer(cell);
    if (solarRate != null) parts.push(formatSolarRatePerKwh(solarRate));
    if (cell.financeMethod) parts.push(cell.financeMethod);
    return parts.join(' · ') || 'Financed';
}

/** Best cell per product column (`recommended` flag from pricing grid). */
export function getRecommendedPicks(rows: FinancePartnerRow[]): CombinedFinancePick[] {
    const picks: CombinedFinancePick[] = [];
    for (const product of FINANCING_PRODUCTS) {
        for (const row of rows) {
            const cell = row.prices[product];
            if (!cell?.recommended) continue;
            const monthly = cell.monthly;
            if (!Number.isFinite(monthly) || monthly <= 0) continue;
            picks.push({
                cellKey: `${row.partner}::${product}`,
                partner: row.partner,
                product,
                cell,
                isCash: row.partner === 'Cash',
            });
            break;
        }
    }
    return picks;
}

export function buildRecommendedCombinedFinancing(rows: FinancePartnerRow[]): CombinedFinancingSummary {
    let picks = getRecommendedPicks(rows);
    
    // We only care about financed options for combined summary (ignore Cash partner)
    picks = picks.filter((pick) => pick.partner !== 'Cash' && pick.cell.monthly > 0);
    
    picks = filterRedundantProductPicks(picks);
    
    const allLines: CombinedFinanceLine[] = [];

    for (const pick of picks) {
        const partnerLabel = partnerLabelForCombined(pick.partner);

        allLines.push({
            id: pick.cellKey,
            partner: pick.partner,
            partnerLabel,
            method: pick.cell.financeMethod ?? '',
            monthly: pick.cell.monthly,
            productTags: [pick.product],
            productLabel: productDisplayName(pick.product),
            detail: formatPickDetail(pick.cell, pick.isCash),
            picks: [pick],
        });
    }

    let totalMonthly = 0;
    for (const line of allLines) {
        totalMonthly += line.monthly;
    }

    const breakdown =
        allLines.length > 0
            ? allLines.map((l) => `${l.partnerLabel} $${l.monthly.toFixed(2)}`).join(' + ')
            : 'Load pricing to see recommended options';

    return {
        totalMonthly,
        lines: allLines,
        breakdown,
    };
}

/**
 * Narrow a combined summary to specific line ids, recomputing total and breakdown.
 * Lets callers quote only the lines a user kept included (AI plans combined-products panel).
 */
export function filterCombinedSummaryByLineIds(
    summary: CombinedFinancingSummary,
    lineIds: string[],
): CombinedFinancingSummary {
    const keep = new Set(lineIds);
    const lines = summary.lines.filter((line) => keep.has(line.id));

    let totalMonthly = 0;
    for (const line of lines) {
        totalMonthly += line.monthly;
    }

    const breakdown =
        lines.length > 0
            ? lines.map((l) => `${l.partnerLabel} $${l.monthly.toFixed(2)}`).join(' + ')
            : 'Load pricing to see recommended options';

    return { totalMonthly, lines, breakdown };
}
